import { Divider, Form, FormInstance, Input, Skeleton, Typography } from 'antd';
import { Rule } from 'antd/es/form';
import { DefaultOptionType } from 'antd/es/select';
import { FormItem, message } from 'components/common';
import { messages, validateMessages } from 'messages';
import { organizationUnitsMessages } from 'messages/organization-units.messages';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import {
  useAddOrganizationUnitMutation,
  useGetOrganizationUnitDetailQuery,
  useUpdateOrganizationUnitMutation
} from 'services/organization-units';
import { CreateOrganizationUnitDto, UserCompactDto } from 'types';
import {
  AUTH_OPTION_TYPES,
  createOrganizationUnitInitialValues,
  MICROSERVICES,
  ORGANIZATION_UNIT_CLASSIFY,
  organizationUnitValidationRules,
  updateOrganizationUnitInitialValues
} from 'utils';
import {
  RadioOptions,
  SelectOrganizationUnits,
  SelectUsers,
  TreeCheckboxOrganizationUnitServiceTypes
} from 'components';
import UpdateStaffList, { UpdatedUserCompactDto } from './UpdateStaffList';
export type OrganizationUnitFormProps = {
  onChangeLoading?: (value: boolean) => void;
  onCreateSuccess?: () => void;
  organizationUnitId?: number;
};

export type OrganizationUnitFormRefProps = {
  form: FormInstance<OrganizationUnitsFormType>;
  isLoading: boolean;
};

export type OrganizationUnitsFormType = Omit<
  CreateOrganizationUnitDto,
  'parentId' | 'userIds'
//  | 'serviceTypeIds'
> & {
  parentId?: DefaultOptionType;
  userIds?: UserCompactDto[];
  // serviceTypeIds: DefaultOptionType[];
  // organizationUnitClassifyId?: DefaultOptionType;
};
const OrganizationUnitForm = forwardRef<OrganizationUnitFormRefProps, OrganizationUnitFormProps>(
  ({ onChangeLoading, onCreateSuccess, organizationUnitId }, ref) => {
    useImperativeHandle(ref, () => ({
      form: form,
      isLoading: isLoadingCreate || isLoadingUpdate
    }));

    const [form] = Form.useForm<OrganizationUnitsFormType>();
    const classifyId = Form.useWatch('organizationUnitClassifyId', form);
    const serviceTypeIds = Form.useWatch('serviceTypeIds', form) || [];
    const parentId = Form.useWatch('parentId', form);
    const { data: organizationUnit, isLoading: isLoadingDetail } = useGetOrganizationUnitDetailQuery(
      organizationUnitId!,
      {
        skip: !organizationUnitId,
        refetchOnMountOrArgChange: true
      }
    );

    const [onCreate, { isLoading: isLoadingCreate }] = useAddOrganizationUnitMutation();
    const [onUpdate, { isLoading: isLoadingUpdate }] = useUpdateOrganizationUnitMutation();

    useEffect(() => {
      if (organizationUnit && organizationUnitId) {
        form.setFieldsValue({
          ...organizationUnit.data,
          parentId: organizationUnit.data?.parent
            ? {
              label: organizationUnit.data?.parent?.name,
              value: organizationUnit.data.parentId
            }
            : undefined,
          deputyUserIds: organizationUnit.data.deputyUsers?.map((user) => user.userId) || []
        });
      }
    }, [organizationUnit, organizationUnitId]);

    const onFinish = (values: OrganizationUnitsFormType) => {
      const data: CreateOrganizationUnitDto = {
        ...values,
        // serviceTypeIds: values?.serviceTypeIds?.map((o) => o.value as number),
        parentId: values.parentId?.value as number,
        userIds: values?.userIds?.map((o) => o.userId)
      };
      if (!organizationUnitId) {
        onCreate(data)
          .unwrap()
          .then((rs) => {
            message.systemSuccess(rs.message);
            onCreateSuccess?.();
          });
      } else {
        onUpdate({
          organizationUnitId,
          ...data
        })
          .unwrap()
          .then((rs) => {
            message.systemSuccess(rs.message);
            onCreateSuccess?.();
          });
      }
    };
    useEffect(() => {
      if (onChangeLoading) {
        onChangeLoading(isLoadingCreate || isLoadingUpdate);
      }
    }, [onChangeLoading, isLoadingCreate, isLoadingUpdate]);

    const validationRules: Partial<Record<keyof OrganizationUnitsFormType, Rule[]>> = {
      ...organizationUnitValidationRules,
      parentId: [{ required: !organizationUnitId }]
    };

    const disabledFormSystem = organizationUnit?.data.isSystem;

    const userIds: UpdatedUserCompactDto[] = Form.useWatch('userIds', form) || [];
    const handleRemove = (record: UpdatedUserCompactDto) => {
      form.setFieldValue(
        'userIds',
        userIds.filter((o) => o.userId !== record.userId)
      );
    };

    const handleAdd = (record: DefaultOptionType & UpdatedUserCompactDto) => {
      form.setFieldValue('userIds', [
        {
          userId: record.userId,
          fullName: record.fullName,
          shortName: record.shortName,
          avatar: record.avatar,
          isAdded: true
        },
        ...userIds.filter((o) => o.userId !== record.userId)
      ]);
    };

    return (
      <Form
        scrollToFirstError={{ behavior: 'smooth', block: 'start' }}
        labelAlign='right'
        labelCol={{
          flex: '180px'
        }}
        requiredMark={false}
        form={form}
        name='organizationUnitForm'
        onFinish={onFinish}
        layout='horizontal'
        validateMessages={validateMessages}
        initialValues={organizationUnitId ? updateOrganizationUnitInitialValues : createOrganizationUnitInitialValues}
      >
        <Skeleton loading={isLoadingDetail}>
          <Typography.Title level={5} className='mb-5 text-lg'>
            {messages.general}
          </Typography.Title>
          <FormItem.FloatLabel<OrganizationUnitsFormType>
            rules={validationRules.name}
            label={organizationUnitsMessages.organizationUnitName}
            name='name'
          >
            <Input disabled={disabledFormSystem} />
          </FormItem.FloatLabel>
          <FormItem.FloatLabel<OrganizationUnitsFormType>
            rules={validationRules.code}
            label={organizationUnitsMessages.code}
            name='code'
          >
            <Input disabled={disabledFormSystem} />
          </FormItem.FloatLabel>

          <Divider className='mt-4' />

          <FormItem.FloatLabel<OrganizationUnitsFormType>
            label={organizationUnitsMessages.upperOrganizationUnit}
            rules={validationRules.parentId}
            name='parentId'
          >
            <SelectOrganizationUnits
              disabled={disabledFormSystem}
              labelInValue
              disableOrganizationUnitId={organizationUnitId ? [organizationUnitId] : undefined}
            />
          </FormItem.FloatLabel>

          <Divider className='mt-4' />

          <Typography.Title level={5} className='required-field mb-5 text-lg'>
            {organizationUnitsMessages.organizationUnitType}
          </Typography.Title>
          <Form.Item<OrganizationUnitsFormType>
            label={organizationUnitsMessages.organizationUnitType}
            rules={validationRules.organizationUnitClassifyId}
            name='organizationUnitClassifyId'
            noStyle
          >
            <RadioOptions
              disabled={disabledFormSystem}
              service={MICROSERVICES.AUTH}
              optionTypeId={AUTH_OPTION_TYPES.ORGANIZATION_UNIT_CLASSIFY}
            />
          </Form.Item>
          <FormItem.ErrorText<OrganizationUnitsFormType> fieldsName={['organizationUnitClassifyId']} form={form} />

          {classifyId === ORGANIZATION_UNIT_CLASSIFY.PHONGDOI && (
            <>
              <Typography.Paragraph className='my-5'>
                {organizationUnitsMessages.headAndDeputyNote}
              </Typography.Paragraph>
              <FormItem.FloatLabel<OrganizationUnitsFormType>
                label={organizationUnitsMessages.headUser}
                rules={validationRules.headUserId}
                name='headUserId'
              >
                <SelectUsers
                  organizationUnitId={[organizationUnitId ?? 0]}
                // selectedOpts={organizationUnit?.data.headUser ? [organizationUnit?.data.headUser] : []}
                />
              </FormItem.FloatLabel>

              <FormItem.FloatLabel<OrganizationUnitsFormType>
                label={organizationUnitsMessages.deputyUser}
                rules={validationRules.deputyUserIds}
                name='deputyUserIds'
              >
                <SelectUsers
                  mode='multiple'
                  organizationUnitId={[organizationUnitId ?? 0]}
                // selectedOpts={organizationUnit?.data.deputyUsers || []}
                />
              </FormItem.FloatLabel>
            </>
          )}
          {!organizationUnitId && (
            <>
              <Divider className='mt-4' />
              <Typography.Title level={5} className='mb-5 text-lg'>
                {organizationUnitsMessages.users}
              </Typography.Title>
              <FormItem<OrganizationUnitsFormType> name='userIds' noStyle />
              <UpdateStaffList users={userIds} onAdd={handleAdd} onRemove={handleRemove} totalUsers={userIds.length} />
            </>
          )}

          <Divider className='mt-4' />

          <Form.Item<OrganizationUnitsFormType> name='serviceTypeIds' />
          {
            classifyId === ORGANIZATION_UNIT_CLASSIFY.PHONGDOI && !disabledFormSystem && (
              <div>
                <Typography.Title level={5} className='mb-5 text-lg'>
                  {organizationUnitsMessages.inchargeService}
                </Typography.Title>
                <Form.Item<OrganizationUnitsFormType>
                  rules={validationRules.serviceTypeIds}
                  label={organizationUnitsMessages.service}
                  name='serviceTypeIds'
                  noStyle
                  shouldUpdate={(prev: OrganizationUnitsFormType, curr: OrganizationUnitsFormType) => {
                    if (curr.organizationUnitClassifyId !== ORGANIZATION_UNIT_CLASSIFY.PHONGDOI) {
                      form.setFieldValue('serviceTypeIds', null);
                    }
                    return prev?.organizationUnitClassifyId !== curr?.organizationUnitClassifyId;
                  }}
                >
                  <TreeCheckboxOrganizationUnitServiceTypes
                    parentId={parentId?.value as number}
                    value={serviceTypeIds}
                    onChange={(value) => form.setFieldValue('serviceTypeIds', value)}
                    apiProps={{ minLevel: 2, isActive: true, excludeOrganizationUnitId: organizationUnitId }}
                  />
                </Form.Item>
                <FormItem.ErrorText<OrganizationUnitsFormType> fieldsName={['serviceTypeIds']} form={form} />
              </div>
            )
          }
        </Skeleton >
      </Form >
    );
  }
);
export default OrganizationUnitForm;
